import {
  AIAssistantApi,
  AddressApi,
  AnalyticsApi,
  AppointmentApi,
  AppointmentRequestApi,
  AsyncAppointmentApi,
  AuthenticationApi,
  AvailabilityApi,
  BenefitsApi,
  BenefitsRecommendationsApi,
  BillingApi,
  CensusApi,
  ChartingApi,
  ClinicalAdminApi,
  ClinicalAppointmentApi,
  CommentsApi,
  CompanyAdminApi,
  CompanyApi,
  CompanyMemberApi,
  CompanySettingsApi,
  EmployersApi,
  EnrollmentsApi,
  EncounterApi,
  EntitlementApi,
  EmployeesApi,
  EPrescribeAuthApi,
  FeaturePolicyApi,
  HouseholdApi,
  LabOrderingApi,
  MedicalApi,
  MembersApi,
  MemberApi,
  ReimbursementRequestsApi,
  MembershipApi,
  MoneyMovementApi,
  NotificationApi,
  NotificationPreferencesApi,
  OnsiteClinicApi,
  PartnersApi,
  PBMApi,
  ProviderApi,
  QuestionnairesApi,
  SurveyApi,
  TagApi,
  TaskApi,
  UrgentCareOrderingApi,
  UserApi,
  UtilityApi,
  WorkforceIntegrationApi,
  YouthMembershipApi,
  CareNavApi,
  IchraQuotingApi,
  InvoicingApi,
  PayrollDeductionsApi,
} from "@vitable/asclepius-api-client-ts"
import { useStateFlow } from "./state-flow"
import asclepiusConfig from "../../config/asclepius-config"
import axios from "axios"
import { createAuthenticatedAsclepiusAxiosInstance } from "./authenticated-asclepius-axios"

/**
 * Specialization of {@link Error}.
 * Base class for all Asclepius API-related errors.
 */
export class AsclepiusApiError extends Error {
  public appErrorCode: string | null
  public cause: any
  public sendToSentryIn: boolean

  constructor(cause: Error, message: string, appErrorCode: string | null, sendToSentryIn: boolean) {
    super(message)
    this.cause = cause
    this.appErrorCode = appErrorCode
    this.sendToSentryIn = sendToSentryIn
  }
}

/**
 * Specialization of {@link AsclepiusApiError}.
 * Indicates that the Asclepius API returned back a non-2XX response.
 */
export class AsclepiusApiResponseError extends AsclepiusApiError {
  public serverTimestamp: string
  public traceId: string

  constructor(cause: Error, message: string, appErrorCode: string | null, traceId: string, serverTimestamp: string) {
    super(cause, message, appErrorCode, false) // Asclepius already reports these when needed
    this.traceId = traceId
    this.serverTimestamp = serverTimestamp
  }
}

export default function useAsclepius() {
    const SERVER_URL = asclepiusConfig.getAsclepiusServerUrl()
    const AGGREGATE_ERROR_MESSAGE_DELIMITER = "\n • "
    const axiosInstance = createAuthenticatedAsclepiusAxiosInstance()

    const getAuthenticationApi = () => new AuthenticationApi(undefined, SERVER_URL, axiosInstance)
    const getCensusApi = () => new CensusApi(undefined, SERVER_URL, axiosInstance)
    const getCompanyApi = () => new CompanyApi(undefined, SERVER_URL, axiosInstance)
    const getCompanyMemberApi = () => new CompanyMemberApi(undefined, SERVER_URL, axiosInstance)
    const getCompanySettingsApi = () => new CompanySettingsApi(undefined, SERVER_URL, axiosInstance)
    const getEmployeesApi = () => new EmployeesApi(undefined, SERVER_URL, axiosInstance)
    const getEmployersApi = () => new EmployersApi(undefined, SERVER_URL, axiosInstance)
    const getMemberApi = () => new MemberApi(undefined, SERVER_URL, axiosInstance)
    const getMembersApi = () => new MembersApi(undefined, SERVER_URL, axiosInstance)
    const getMembershipApi = () => new MembershipApi(undefined, SERVER_URL, axiosInstance)
    const getBillingApi = () => new BillingApi(undefined, SERVER_URL, axiosInstance)
    const getMedicalApi = () => new MedicalApi(undefined, SERVER_URL, axiosInstance)
    const getAppointmentApi = () => new AppointmentApi(undefined, SERVER_URL, axiosInstance)
    const getAvailabilityApi = () => new AvailabilityApi(undefined, SERVER_URL, axiosInstance)
    const getClinicalAppointmentApi = () => new ClinicalAppointmentApi(undefined, SERVER_URL, axiosInstance)
    const getCompanyAdminApi = () => new CompanyAdminApi(undefined, SERVER_URL, axiosInstance)
    const getHouseholdApi = () => new HouseholdApi(undefined, SERVER_URL, axiosInstance)
    const getLabOrderingApi = () => new LabOrderingApi(undefined, SERVER_URL, axiosInstance)
    const getOnsiteClinicApi = () => new OnsiteClinicApi(undefined, SERVER_URL, axiosInstance)
    const getSurveyApi = () => new SurveyApi(undefined, SERVER_URL, axiosInstance)
    const getUrgentCareOrderingApi = () => new UrgentCareOrderingApi(undefined, SERVER_URL, axiosInstance)
    const getTagApi = () => new TagApi(undefined, SERVER_URL, axiosInstance)
    const getBenefitsApi = () => new BenefitsApi(undefined, SERVER_URL, axiosInstance)
    const getEnrollmentsApi = () => new EnrollmentsApi(undefined, SERVER_URL, axiosInstance)
    const getBenefitsRecommendationsApi = () => new BenefitsRecommendationsApi(undefined, SERVER_URL, axiosInstance)
    const getEntitlementsApi = () => new EntitlementApi(undefined, SERVER_URL, axiosInstance)
    const getUserApi = () => new UserApi(undefined, SERVER_URL, axiosInstance)
    const getWorkforceIntegrationApi = () => new WorkforceIntegrationApi(undefined, SERVER_URL, axiosInstance)
    const getYouthMembershipApi = () => new YouthMembershipApi(undefined, SERVER_URL, axiosInstance)
    const getNotificationApi = () => new NotificationApi(undefined, SERVER_URL, axiosInstance)
    const getAnalyticsApi = () => new AnalyticsApi(undefined, SERVER_URL, axiosInstance)
    const getPartnersApi = () => new PartnersApi(undefined, SERVER_URL, axiosInstance)
    const getPayrollDeductionsApi = () => new PayrollDeductionsApi(undefined, SERVER_URL, axiosInstance)
    const getEntitlementApi = () => new EntitlementApi(undefined, SERVER_URL, axiosInstance)
    const getEPrescribeAuthApi = () => new EPrescribeAuthApi(undefined, SERVER_URL, axiosInstance)
    const getAppointmentRequestApi = () => new AppointmentRequestApi(undefined, SERVER_URL, axiosInstance)
    const getEncounterApi = () => new EncounterApi(undefined, SERVER_URL, axiosInstance)
    const getFeaturePolicyApi = () => new FeaturePolicyApi(undefined, SERVER_URL, axiosInstance)
    const getClinicalAdminApi = () => new ClinicalAdminApi(undefined, SERVER_URL, axiosInstance)
    const getNotificationPreferencesApi = () => new NotificationPreferencesApi(undefined, SERVER_URL, axiosInstance)
    const getCommentsApi = () => new CommentsApi(undefined, SERVER_URL, axiosInstance)
    const getTaskApi = () => new TaskApi(undefined, SERVER_URL, axiosInstance)
    const getUtilityApi = () => new UtilityApi(undefined, SERVER_URL, axiosInstance)
    const getProviderApi = () => new ProviderApi(undefined, SERVER_URL, axiosInstance)
    const getChartingApi = () => new ChartingApi(undefined, SERVER_URL, axiosInstance)
    const getPbmApi = () => new PBMApi(undefined, SERVER_URL, axiosInstance)
    const getQuestionnairesApi = () => new QuestionnairesApi(undefined, SERVER_URL, axiosInstance)
    const getAsyncAppointmentApi = () => new AsyncAppointmentApi(undefined, SERVER_URL, axiosInstance)
    const getCareNavApi = () => new CareNavApi(undefined, SERVER_URL, axiosInstance)
    const getIchraQuotingApi = () => new IchraQuotingApi(undefined, SERVER_URL, axiosInstance)
    const getMoneyMovementApi = () => new MoneyMovementApi(undefined, SERVER_URL, axiosInstance)
    const getReimbursementRequestsApi = () => new ReimbursementRequestsApi(undefined, SERVER_URL, axiosInstance)
    const getInvoicingApi = () => new InvoicingApi(undefined, SERVER_URL, axiosInstance)
    const getAddressApi = () => new AddressApi(undefined, SERVER_URL, axiosInstance)
    const getAIAssistantApi = () => new AIAssistantApi(undefined, SERVER_URL, axiosInstance)

    const { result, setNilResult, setLoadingResult, setFailureResult, setSuccessResult } = useStateFlow<any>()

    async function callWithResponse<T>(fn: () => Promise<any>): Promise<T> {
      setLoadingResult()
      try {
        const data: T = (await fn()).data
        setSuccessResult(data)
        return data
      } catch (e: any) {
        const cause: Error = translateAsclepiusApiCallError(e)
        setFailureResult(cause)
        throw cause
      }
    }

    /**
     * Settles all given promises parallel, and either returns the results or throws an AggregateError with all seen failures
     * @param requestPromises
     * @returns Array of responses
     */
    async function callAndSettleAllInParallel<T>(
      requestPromises: Array<() => Promise<any>>,
      aggregateErrorMessage: string | null = null
    ): Promise<Array<T>> {
      setLoadingResult()

      // Note: allSettled() is distinct from all() — it does not throw, and waits for all promises to settle instead of rejecting at the first error.
      const results = await Promise.allSettled(
        requestPromises.map(async (requestFn) => {
          const response = await requestFn()
          return response.data
        })
      )

      const errors: Array<Error> = []
      const responses: Array<T> = []

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          responses.push(result.value)
        } else {
          const e = result.reason

          const cause: Error = translateAsclepiusApiCallError(e)
          setFailureResult(cause)

          errors.push(cause)
        }
      })

      if (errors.length > 0) {
        let message: string

        if (aggregateErrorMessage) {
          message = aggregateErrorMessage
        } else if (errors.length === 1) {
          message = errors[0].message
        } else {
          message = `${errors.length} operations failed:${AGGREGATE_ERROR_MESSAGE_DELIMITER}${errors
            .map((e) => e.message)
            .join(AGGREGATE_ERROR_MESSAGE_DELIMITER)}`
        }

        const aggregateError = new AggregateError(errors, message)

        setFailureResult(aggregateError)
        throw aggregateError
      }

      setSuccessResult(responses)
      return responses
    }

    return {
      callWithResponse,
      callAndSettleAllInParallel,
      resetCallResult: setNilResult,
      callResult: result,
      axiosInstance,
      getAuthenticationApi,
      getCensusApi,
      getCompanyApi,
      getCompanyMemberApi,
      getCompanySettingsApi,
      getEmployeesApi,
      getMemberApi,
      getMembershipApi,
      getBillingApi,
      getMedicalApi,
      getAppointmentApi,
      getAvailabilityApi,
      getClinicalAppointmentApi,
      getCompanyAdminApi,
      getHouseholdApi,
      getLabOrderingApi,
      getOnsiteClinicApi,
      getSurveyApi,
      getUrgentCareOrderingApi,
      getTagApi,
      getBenefitsApi,
      getEnrollmentsApi,
      getMembersApi,
      getEmployersApi,
      getBenefitsRecommendationsApi,
      getEntitlementsApi,
      getUserApi,
      getWorkforceIntegrationApi,
      getYouthMembershipApi,
      getNotificationApi,
      getAnalyticsApi,
      getPartnersApi,
      getPayrollDeductionsApi,
      getEntitlementApi,
      getEPrescribeAuthApi,
      getAppointmentRequestApi,
      getEncounterApi,
      getFeaturePolicyApi,
      getClinicalAdminApi,
      getNotificationPreferencesApi,
      getCommentsApi,
      getTaskApi,
      getUtilityApi,
      getProviderApi,
      getChartingApi,
      getPbmApi,
      getQuestionnairesApi,
      getAsyncAppointmentApi,
      getCareNavApi,
      getIchraQuotingApi,
      getMoneyMovementApi,
      getInvoicingApi,
      getReimbursementRequestsApi,
      getAddressApi,
      getAIAssistantApi,
    }
}

/**
 * Translates Axios errors from Asclepius API calls into structured error objects
 * with user-friendly messages and appropriate Sentry reporting behavior.
 */
function translateAsclepiusApiCallError(ex: Error): Error {
  if (!axios.isAxiosError(ex)) {
    return ex
  }

  // For server-side problems, ensure that these are ignored on Sentry (Asclepius
  // will have reported it already if necessary) and ensure that these errors are
  // legible.
  const asclepiusErrorData = ex.response?.data
  if (asclepiusErrorData) {
    return new AsclepiusApiResponseError(
      ex,
      asclepiusErrorData["message"],
      asclepiusErrorData["app_error_code"],
      asclepiusErrorData["trace_id"],
      asclepiusErrorData["timestamp"]
    )
  }

  // For network connectivity problems, ensure that these are ignored on
  // Sentry (user probably just needs to connect to WI-FI) and ensure that
  // these errors are legible.
  if (ex.message == "Network Error") {
    return new AsclepiusApiError(
      ex,
      "Cannot connect to Vitable system… are you connected to the internet?",
      "network",
      false
    )
  }

  // For any other unknown problems, ensure that these do go to Sentry
  // because something weird is happening on the client-side that may
  // need fixed and may not be reported to Sentry by Asclepius
  return ex
}

/**
 * Determines if the given error is an ignorable Asclepius error.
 * @param ex the error to be checked
 *
 * @returns {@code true} if it should be ignored; otherwise {@code false}
 */
export function isIgnorableAsclepiusApiError(ex: unknown): boolean {
  return ex instanceof AsclepiusApiError && !ex.sendToSentryIn
}
